/**
 * Sections API — User Section Memberships (Section-keyed)
 *
 * Two routes share this response shape:
 *  - GET /v1/users/me/sections                                  (current user)
 *  - GET /v1/schools/:schoolId/school-users/:userId/section-memberships  (admin)
 *
 * Distinct from the older `/v1/schools/:schoolId/school-users/:userId/sections`
 * endpoint (still in use for the Classes tab with `offering_type=ACADEMIC`).
 * This endpoint is membership-keyed: it enumerates per-mirror audience/role/
 * permissions for each section the user belongs to, collapsed to one entry
 * per lead section with sub-section instances nested.
 *
 * Plan: docs/plans/2026-05-18-001-refactor-deprecate-usergroup-ui-for-sections-plan.md (U2).
 */

import { z } from 'zod'

export const SectionMembershipPermissionsSchema = z.object({
  canView: z.boolean(),
  canReact: z.boolean(),
  canComment: z.boolean(),
  canPost: z.boolean(),
  canModerate: z.boolean(),
  canInvite: z.boolean()
})
export type SectionMembershipPermissions = z.infer<typeof SectionMembershipPermissionsSchema>

export const SectionMembershipEntrySchema = z.object({
  /** UserGroup mirror publicId — wire ID for publish-target pickers (U7). */
  groupPublicId: z.string(),
  audience: z.string().nullable(),
  sectionRole: z.string().nullable(),
  identityRole: z.string().nullable(),
  memberOrigin: z.string().nullable(),
  permissions: SectionMembershipPermissionsSchema,
  isLeader: z.boolean(),
  isHeadRole: z.boolean(),
  /**
   * Manual section-moderator grant (plan 2026-06-30-001, R20). Present only on
   * the canonical path and only for grant-authorized callers; `canModerate`
   * already folds the grant in via `effectiveModerator`. Optional for
   * back-compat with clients/paths that do not emit it.
   */
  manualGrant: z.boolean().optional()
})
export type SectionMembershipEntry = z.infer<typeof SectionMembershipEntrySchema>

export const SectionCanonicalRefSchema = z.object({
  id: z.string(),
  publicId: z.string(),
  name: z.string(),
  platformId: z.string().nullable(),
  platformLeadSectionId: z.string().nullable(),
  offeringType: z.string().nullable(),
  displayName: z.string().nullable(),
  descriptionOverride: z.string().nullable(),
  bannerBackgroundColor: z.string().nullable(),
  bannerLogoUrl: z.string().nullable(),
  bannerImageUrl: z.string().nullable(),
  avatarImageUrl: z.string().nullable(),
  designTheme: z.unknown(),
  emailSlug: z.string().nullable(),
  betaTestersOnly: z.boolean(),
  schoolYear: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      platformId: z.string().nullable()
    })
    .nullable(),
  schoolLevel: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      platformId: z.string().nullable(),
      abbreviation: z.string().nullable()
    })
    .nullable(),
  gradeLevel: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      platformId: z.string().nullable(),
      abbreviation: z.string().nullable()
    })
    .nullable(),
  course: z
    .object({
      id: z.string(),
      platformId: z.string().nullable(),
      code: z.string().nullable(),
      title: z.string().nullable(),
      description: z.string().nullable()
    })
    .nullable(),
  term: z
    .object({
      id: z.string(),
      platformId: z.string().nullable(),
      description: z.string().nullable(),
      beginDate: z.string().nullable(),
      endDate: z.string().nullable()
    })
    .nullable()
})
export type SectionCanonicalRef = z.infer<typeof SectionCanonicalRefSchema>

const BaseUserSectionMembershipEntry = z.object({
  section: SectionCanonicalRefSchema,
  /** Live count from Section.personCount — NEVER UserGroup.memberCount. */
  personCount: z.number(),
  showInApp: z.boolean(),
  memberships: z.array(SectionMembershipEntrySchema)
})

// Recursive nesting for sub-sections.
export interface UserSectionMembershipEntry extends z.infer<typeof BaseUserSectionMembershipEntry> {
  subSections: UserSectionMembershipEntry[]
}

export const UserSectionMembershipEntrySchema: z.ZodType<UserSectionMembershipEntry> =
  BaseUserSectionMembershipEntry.extend({
    subSections: z.lazy(() => z.array(UserSectionMembershipEntrySchema))
  })

export const ListUserSectionMembershipsResponseDataSchema = z.object({
  data: z.array(UserSectionMembershipEntrySchema),
  meta: z.object({
    total: z.number(),
    /**
     * Cursor for the next page of results. Only the school-wide variant
     * (U7 admin picker) paginates; per-user variants always return `null`.
     * Cursor is the `section.publicId` of the last entry on this page —
     * pass it back as `?cursor=...` to fetch the next slice.
     */
    nextCursor: z.string().nullable().optional()
  })
})
export type ListUserSectionMembershipsResponseData = z.infer<typeof ListUserSectionMembershipsResponseDataSchema>

export const ListUserSectionMembershipsResponseSchema = z.object({
  message: z.string(),
  data: ListUserSectionMembershipsResponseDataSchema
})
export type ListUserSectionMembershipsResponse = z.infer<typeof ListUserSectionMembershipsResponseSchema>
